// src/pages/admin/batches/ViewBatches.tsx
import React, { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useAdminBatchStore, Batch } from "src/store/adminBatchStore";
import { useBatchManagementUIStore } from "src/store/batchManagementUIStore";
import BatchList from "src/components/admin/batches/BatchList";
import { BatchInspectorDrawer } from "src/components/admin/batches/BatchInspectorDrawer";
import { AssignTutorModal } from "src/components/admin/batches/AssignTutorModal";
import ConfirmationModal from "src/components/ui/ConfirmationModal";
import { useToast } from "src/hooks/useToast";
import { Button } from "src/components/ui/Button";
import { Input } from "src/components/ui/Input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "src/components/ui/Select";
import {
  Search,
  Plus,
  Download,
  AlertCircle,
  AlertTriangle,
  RotateCcw,
  CheckCircle,
} from "lucide-react";
import LoadingSpinner from "src/components/LoadingSpinner";
import { usePageHeaderStore } from "src/store/pageHeaderStore";
import { ROUTES } from "src/routes/RouteConfig";

type QuickFilterType = "all" | "active" | "unassigned" | "low_balance" | "completed";

const ViewBatches: React.FC = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { setTitle, setDescription, setActions, reset } = usePageHeaderStore();

  const {
    batches,
    isLoading,
    error,
    fetchBatches,
    updateBatch,
  } = useAdminBatchStore();

  const {
    searchTerm,
    setSearchTerm,
    selectedBatchId,
    setSelectedBatchId,
  } = useBatchManagementUIStore();

  // Local filter states
  const [statusFilter, setStatusFilter] = useState<QuickFilterType>("active");
  const [productFilter, setProductFilter] = useState<string>("all");

  // Modals state
  const [isAssignModalOpen, setIsAssignModalOpen] = useState(false);
  const [confirmAction, setConfirmAction] = useState<{
    isOpen: boolean;
    batchId: string;
    action: "cancelled" | "completed";
  }>({
    isOpen: false,
    batchId: "",
    action: "cancelled",
  });

  // Set Page Header
  useEffect(() => {
    setTitle("Student Batches");
    setDescription("Monitor curriculum pacing, assign certified tutors, and triage session depletion alerts across all cohorts.");
    setActions(null);

    return () => reset();
  }, [setTitle, setDescription, setActions, reset]);

  // Fetch batches on mount
  useEffect(() => {
    void fetchBatches();
  }, [fetchBatches]);

  // Telemetry KPI calculations
  const stats = useMemo(() => {
    const total = batches.length;
    const active = batches.filter((b) => b.status === "active").length;
    const unassigned = batches.filter(
      (b) => b.status === "active" && (!b.default_tutor_id || !b.tutor_name)
    ).length;
    const lowBalance = batches.filter(
      (b) =>
        b.status === "active" &&
        b.remaining_sessions !== undefined &&
        b.remaining_sessions <= 2 &&
        b.remaining_sessions > 0
    ).length;
    const completed = batches.filter((b) => b.status === "completed").length;

    return { total, active, unassigned, lowBalance, completed };
  }, [batches]);

  // Filter batches based on search, status, and product
  const filteredBatches = useMemo(() => {
    return batches.filter((batch) => {
      const search = searchTerm.toLowerCase();
      const matchesSearch =
        !searchTerm ||
        batch.name?.toLowerCase().includes(search) ||
        batch.student_name?.toLowerCase().includes(search) ||
        batch.subject_name?.toLowerCase().includes(search) ||
        batch.tutor_name?.toLowerCase().includes(search) ||
        batch.package_name?.toLowerCase().includes(search) ||
        batch.package_type?.toLowerCase().includes(search) ||
        batch.id?.toLowerCase().includes(search);

      // Status filter
      let matchesStatus = true;
      if (statusFilter === "active") {
        matchesStatus = batch.status === "active";
      } else if (statusFilter === "unassigned") {
        matchesStatus = batch.status === "active" && (!batch.default_tutor_id || !batch.tutor_name);
      } else if (statusFilter === "low_balance") {
        matchesStatus =
          batch.status === "active" &&
          batch.remaining_sessions !== undefined &&
          batch.remaining_sessions <= 2 &&
          batch.remaining_sessions > 0;
      } else if (statusFilter === "completed") {
        matchesStatus = batch.status === "completed";
      }

      // Product filter
      let matchesProduct = true;
      if (productFilter !== "all") {
        const prod = (batch.package_name || batch.package_type || "").toLowerCase();
        matchesProduct = prod.includes(productFilter.toLowerCase());
      }

      return matchesSearch && matchesStatus && matchesProduct;
    });
  }, [batches, searchTerm, statusFilter, productFilter]);

  // Keep selectedBatch in sync with filtered list
  useEffect(() => {
    if (!selectedBatchId && filteredBatches.length > 0) {
      setSelectedBatchId(filteredBatches[0].id);
    } else if (selectedBatchId && !filteredBatches.some((b) => b.id === selectedBatchId)) {
      if (filteredBatches.length > 0) {
        setSelectedBatchId(filteredBatches[0].id);
      } else {
        setSelectedBatchId(null);
      }
    }
  }, [filteredBatches, selectedBatchId, setSelectedBatchId]);

  const selectedBatch = useMemo(() => {
    return batches.find((b) => b.id === selectedBatchId) || null;
  }, [batches, selectedBatchId]);

  // Action handlers
  const handleConfirmAction = async () => {
    const { batchId, action } = confirmAction;
    setConfirmAction({ isOpen: false, batchId: "", action: "cancelled" });

    const updated = await updateBatch(batchId, { status: action });
    if (updated) {
      toast({
        title: `Batch ${action}`,
        description: `The cohort was successfully marked as ${action}.`,
        type: "success",
      });
      return;
    }

    toast({
      title: `Unable to update batch`,
      description: "Please check your network connection and try again.",
      type: "error",
    });
  };

  const handleExportCSV = () => {
    if (filteredBatches.length === 0) {
      toast({
        title: "Export Failed",
        description: "No batch records match the current filter criteria.",
        type: "warning",
      });
      return;
    }

    const headers = ["Batch Name", "Student", "Subject", "Product", "Tutor", "Status", "Total Sessions", "Remaining Sessions", "Created Date"];
    const rows = filteredBatches.map((b) => [
      `"${b.name || 'Unnamed Batch'}"`,
      `"${b.student_name || ''}"`,
      `"${b.subject_name || ''}"`,
      `"${b.package_name || b.package_type || ''}"`,
      `"${b.tutor_name || 'Unassigned'}"`,
      b.status,
      b.total_sessions ?? 0,
      b.remaining_sessions ?? 0,
      b.created_at,
    ]);

    const csvContent = "data:text/csv;charset=utf-8," + [headers.join(","), ...rows.map((e) => e.join(","))].join("\n");
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", `rflearn_batches_${new Date().toISOString().slice(0, 10)}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div className="space-y-6 pb-12">
      {/* Top Operational Bar: Breadcrumbs (Left) & Actions (Right) */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 pb-1">
        <div className="flex items-center gap-1.5 text-xs text-slate-500">
          <button
            type="button"
            onClick={() => navigate("/admin/batches")}
            className="hover:text-purple-700 transition"
          >
            Academic Operations
          </button>
          <span>/</span>
          <button
            type="button"
            onClick={() => navigate(ROUTES.ADMIN_BATCHES.path)}
            className="hover:text-purple-700 transition"
          >
            Batch Hub
          </button>
          <span>/</span>
          <span className="font-semibold text-slate-800">All Batches</span>
        </div>

        <div className="flex items-center gap-2 self-stretch sm:self-auto justify-end">
          <Button
            size="sm"
            onClick={() => navigate(ROUTES.ADMIN_CREATE_BATCH.path)}
            className="h-9 px-3.5 text-xs font-semibold bg-purple-600 hover:bg-purple-700 text-white flex items-center gap-1.5 shadow-2xs"
          >
            <Plus className="h-3.5 w-3.5" />
            Create Batch
          </Button>
        </div>
      </div>

      {isLoading ? (
        <div className="flex flex-col items-center justify-center h-64 bg-white rounded-xl border border-slate-200 shadow-sm">
          <LoadingSpinner />
          <p className="mt-4 text-xs text-slate-500 font-medium">Loading batch operations telemetry...</p>
        </div>
      ) : error ? (
        <div className="text-center py-10 px-4 bg-white rounded-xl border border-rose-200 shadow-sm">
          <AlertCircle className="h-10 w-10 text-rose-500 mx-auto mb-3" />
          <h3 className="text-sm font-bold text-slate-900">Failed to Load Batches</h3>
          <p className="text-xs text-slate-500 mt-1 max-w-md mx-auto">{error}</p>
          <Button
            variant="outline"
            size="sm"
            className="mt-4 text-xs"
            onClick={() => void fetchBatches()}
          >
            Try Again
          </Button>
        </div>
      ) : (
        <>
          {/* ========================================================================= */}
          {/* TOP SECTION: STAT CARDS & FILTERS */}
          {/* ========================================================================= */}
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
            {/* 4 Stat KPI Cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 divide-y md:divide-y-0 md:divide-x divide-slate-200 bg-white border-b border-slate-200">
              {/* Active Pacing */}
              <div
                onClick={() => setStatusFilter("active")}
                className={`p-4 transition cursor-pointer group ${
                  statusFilter === "active" ? "bg-purple-50/50" : "hover:bg-slate-50/60"
                }`}
              >
                <div className="flex items-center justify-between text-xs text-slate-500 font-medium mb-1">
                  <span className="group-hover:text-purple-700 transition">Active Pacing</span>
                  <span className="w-2 h-2 rounded-full bg-emerald-500"></span>
                </div>
                <div className="text-2xl font-bold text-slate-900 tracking-tight">{stats.active}</div>
                <div className="text-xs text-slate-500 mt-1 flex items-center gap-1">
                  <span className="text-emerald-700 font-semibold font-mono">
                    {stats.total > 0 ? Math.round((stats.active / stats.total) * 100) : 0}%
                  </span>{" "}
                  of all cohorts
                </div>
              </div>

              {/* Unassigned Tutors (Action Required) */}
              <div
                onClick={() => setStatusFilter("unassigned")}
                className={`p-4 transition cursor-pointer group ${
                  statusFilter === "unassigned" ? "bg-amber-100/60" : "bg-amber-50/30 hover:bg-amber-50/60"
                }`}
              >
                <div className="flex items-center justify-between text-xs text-amber-800 font-medium mb-1">
                  <span className="font-semibold group-hover:underline">Unassigned Tutors</span>
                  <span className="px-1.5 py-0.5 text-xs font-bold rounded bg-amber-100 text-amber-900 border border-amber-300">
                    Action Req
                  </span>
                </div>
                <div className="text-2xl font-bold text-amber-900 tracking-tight">{stats.unassigned}</div>
                <div className="text-xs text-amber-700 mt-1">Pending instructor matching</div>
              </div>

              {/* Low Balance (< 2 left) */}
              <div
                onClick={() => setStatusFilter("low_balance")}
                className={`p-4 transition cursor-pointer group ${
                  statusFilter === "low_balance" ? "bg-rose-100/60" : "bg-rose-50/20 hover:bg-rose-50/50"
                }`}
              >
                <div className="flex items-center justify-between text-xs text-rose-800 font-medium mb-1">
                  <span className="font-semibold group-hover:underline">Low Balance (&lt; 2 left)</span>
                  <AlertTriangle className="w-3.5 h-3.5 text-rose-600" />
                </div>
                <div className="text-2xl font-bold text-rose-900 tracking-tight">{stats.lowBalance}</div>
                <div className="text-xs text-rose-700 mt-1">Ready for package extension</div>
              </div>

              {/* Completed Batches */}
              <div
                onClick={() => setStatusFilter("completed")}
                className={`p-4 transition cursor-pointer group ${
                  statusFilter === "completed" ? "bg-slate-100/80" : "hover:bg-slate-50/60"
                }`}
              >
                <div className="flex items-center justify-between text-xs text-slate-500 font-medium mb-1">
                  <span className="group-hover:text-slate-900 transition">Completed Batches</span>
                  <CheckCircle className="w-3.5 h-3.5 text-slate-400" />
                </div>
                <div className="text-2xl font-bold text-slate-900 tracking-tight">{stats.completed}</div>
                <div className="text-xs text-slate-500 mt-1">All objectives achieved</div>
              </div>
            </div>

            {/* Filter Ribbon */}
            <div className="p-4 bg-white flex flex-col md:flex-row items-stretch md:items-center justify-between gap-3">
              {/* Search Bar */}
              <div className="relative flex-1 max-w-md">
                <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
                <Input
                  placeholder="Search student, tutor, batch ID, or subject..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-9 text-xs h-9"
                />
              </div>

              {/* Selectors */}
              <div className="flex flex-wrap items-center gap-2">
                {/* Status selector */}
                <div className="flex items-center border border-slate-300 rounded-lg bg-white overflow-hidden text-xs">
                  <span className="px-2.5 py-1.5 bg-slate-50 text-slate-500 border-r border-slate-200 font-medium">
                    Status
                  </span>
                  <Select
                    value={statusFilter}
                    onValueChange={(val) => setStatusFilter(val as QuickFilterType)}
                  >
                    <SelectTrigger className="border-0 h-8 text-xs focus:ring-0">
                      <SelectValue placeholder="Status" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Statuses ({stats.total})</SelectItem>
                      <SelectItem value="active">Active Only ({stats.active})</SelectItem>
                      <SelectItem value="unassigned">Unassigned Tutors ({stats.unassigned})</SelectItem>
                      <SelectItem value="low_balance">Low Balance ({stats.lowBalance})</SelectItem>
                      <SelectItem value="completed">Completed ({stats.completed})</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Product selector */}
                <div className="flex items-center border border-slate-300 rounded-lg bg-white overflow-hidden text-xs">
                  <span className="px-2.5 py-1.5 bg-slate-50 text-slate-500 border-r border-slate-200 font-medium">
                    Product
                  </span>
                  <Select value={productFilter} onValueChange={setProductFilter}>
                    <SelectTrigger className="border-0 h-8 text-xs focus:ring-0">
                      <SelectValue placeholder="Product" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Products</SelectItem>
                      <SelectItem value="booster">High Dosage Booster</SelectItem>
                      <SelectItem value="custom">Personalized Custom</SelectItem>
                      <SelectItem value="prep">Test Preparation</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Reset Filters */}
                {(searchTerm || statusFilter !== "active" || productFilter !== "all") && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      setSearchTerm("");
                      setStatusFilter("active");
                      setProductFilter("all");
                    }}
                    className="text-xs text-slate-500 hover:text-slate-800 h-8 px-2"
                  >
                    <RotateCcw className="w-3.5 h-3.5 mr-1" />
                    Reset
                  </Button>
                )}

                {/* Export CSV button */}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleExportCSV}
                  className="text-xs text-slate-700 h-8 flex items-center gap-1.5"
                >
                  <Download className="w-3.5 h-3.5 text-slate-500" />
                  Export CSV
                </Button>
              </div>
            </div>
          </div>

          {/* ========================================================================= */}
          {/* MASTER-DETAIL SPLIT WORKSPACE */}
          {/* ========================================================================= */}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-start">
            {/* Left Queue: 7 Columns */}
            <div className="lg:col-span-7">
              <BatchList
                batches={filteredBatches}
                selectedBatchId={selectedBatchId}
                onSelectBatch={(id) => setSelectedBatchId(id)}
              />
            </div>

            {/* Right Inspector Drawer: 5 Columns */}
            <div className="lg:col-span-5">
              <BatchInspectorDrawer
                batch={selectedBatch}
                onAssignTutor={() => setIsAssignModalOpen(true)}
                onExtend={(batchId) => navigate(`/admin/batches/${batchId}/extend`)}
                onEdit={(batchId) => navigate(`/admin/batches/edit/${batchId}`)}
                onCancel={(batchId) =>
                  setConfirmAction({
                    isOpen: true,
                    batchId,
                    action: "cancelled",
                  })
                }
                onComplete={(batchId) =>
                  setConfirmAction({
                    isOpen: true,
                    batchId,
                    action: "completed",
                  })
                }
              />
            </div>
          </div>
        </>
      )}

      {/* Tutor Assignment Modal */}
      <AssignTutorModal
        isOpen={isAssignModalOpen}
        onClose={() => setIsAssignModalOpen(false)}
        batch={selectedBatch}
      />

      {/* Action Confirmation Modal */}
      <ConfirmationModal
        isOpen={confirmAction.isOpen}
        onClose={() => setConfirmAction({ isOpen: false, batchId: "", action: "cancelled" })}
        title={confirmAction.action === "cancelled" ? "Cancel Student Batch?" : "Complete Student Batch?"}
        description={
          confirmAction.action === "cancelled"
            ? "Cancelling this batch will halt all remaining sessions and update the cohort state to cancelled. This action cannot be easily undone."
            : "Completing this batch marks all curriculum objectives and sessions as delivered. The cohort will be archived."
        }
        primaryAction={{
          label: confirmAction.action === "cancelled" ? "Yes, Cancel Batch" : "Yes, Complete Batch",
          variant: confirmAction.action === "cancelled" ? "destructive" : "default",
          onClick: handleConfirmAction,
        }}
        secondaryAction={{
          label: "Dismiss",
          variant: "outline",
          onClick: () => setConfirmAction({ isOpen: false, batchId: "", action: "cancelled" }),
        }}
      />
    </div>
  );
};

export default ViewBatches;
